/**
 * Consent handler orchestrator: WP Consent, GCM, CookieBot, Turnstile sync.
 * @package AdvancedTrackingManager
 */

import { onReady, waitFor } from '@lib/dom';
import { createLogger } from '@lib/logger';
import { updateWPConsent } from './wp-consent';
import {
  ensureGtagAndDataLayerWrapped,
  updateGoogleConsentMode,
} from './gcm';
import { waitForCookieBot, getCookieBotConsent, isCookieBotAvailable } from './cookiebot';

const w = globalThis as unknown as Window;

const run = (): void => {
  if (typeof w.atmConsent === 'undefined') return;

  const config = w.atmConsent;
  const debugMode = config.debugMode ?? false;
  const log = createLogger('ATM Consent Handler', debugMode);
  const shouldAddGtagDebugMode = debugMode && (config.atmAddsGtag ?? false);

  const ensureWpSetConsentWrapped = (): void => {
    if (w._atmWpSetConsentWrapped) return;
    if (typeof w.wp_set_consent !== 'function') return;
    w._atmWpSetConsentWrapped = true;
    const original = w.wp_set_consent;
    w.wp_set_consent = (category: string, value: string) => {
      const out =
        config.turnstileConfigured &&
        w._atmTurnstileFailed === true &&
        category !== 'necessary' &&
        category !== 'functional' &&
        value === 'allow'
          ? 'deny'
          : value;
      return original(category, out);
    };
  };
  ensureWpSetConsentWrapped();
  onReady(ensureWpSetConsentWrapped);

  ensureGtagAndDataLayerWrapped(config, shouldAddGtagDebugMode);
  onReady(() => ensureGtagAndDataLayerWrapped(config, shouldAddGtagDebugMode));
  for (const ms of [50, 100, 200, 300, 500, 1000]) {
    setTimeout(() => ensureGtagAndDataLayerWrapped(config, shouldAddGtagDebugMode), ms);
  }

  const isTurnstileScriptLoaded = (): boolean =>
    typeof w.atmTurnstile !== 'undefined' || typeof w.atmTurnstileStatus !== 'undefined';

  const canSetConsent = (): boolean => {
    if (typeof w.atmTurnstileStatus !== 'undefined') {
      const status = w.atmTurnstileStatus!;
      if (status.verified === true && status.serverVerified === true && status.failed === false) {
        w._atmTurnstileFailed = false;
        sessionStorage.setItem('atm_turnstile_failed', 'false');
      } else if (status.failed === true) {
        w._atmTurnstileFailed = true;
        sessionStorage.setItem('atm_turnstile_failed', 'true');
        return false;
      }
    }
    if (w._atmTurnstileFailed === true) return false;
    if (!isTurnstileScriptLoaded()) return true;
    if (typeof w.atmTurnstileStatus === 'undefined') return false;
    const status = w.atmTurnstileStatus!;
    if (sessionStorage.getItem('atm_turnstile_failed') === 'true') return false;
    if (status.pending) return false;
    if (status.failed) return false;
    if (status.verified === true && status.serverVerified === true) return true;
    if (status.verified === true && !status.serverVerified) return false;
    return false;
  };

  const shouldApplyRowDefaultConsent = (): boolean =>
    !config.consentRegionGdpr && !config.consentRegionCalifornia;

  const applyRowDefaultConsent = (): void => {
    if (!config.turnstileConfigured) return;
    const rowDefault = config.consentDefaultRestOfWorld || 'granted';
    const isGranted = rowDefault === 'granted';
    log.debug('Applying ROW default consent (Turnstile verified)', {
      consentDefaultRestOfWorld: rowDefault,
    });
    const gcmUpdates: Record<string, string> = {
      ad_storage: isGranted ? 'granted' : 'denied',
      ad_user_data: isGranted ? 'granted' : 'denied',
      ad_personalization: isGranted ? 'granted' : 'denied',
      analytics_storage: isGranted ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: isGranted ? 'granted' : 'denied',
      security_storage: 'granted',
    };
    updateGoogleConsentMode(gcmUpdates, config);
    if (typeof w.gtag === 'function') {
      try {
        w.gtag('consent', 'update', gcmUpdates);
      } catch {
        log.debug('⚠️ gtag consent update error');
      }
    }
    if (typeof w.wp_set_consent === 'function') {
      w.wp_set_consent('necessary', 'allow');
      w.wp_set_consent('preferences', isGranted ? 'allow' : 'deny');
      w.wp_set_consent('statistics', isGranted ? 'allow' : 'deny');
      w.wp_set_consent('marketing', isGranted ? 'allow' : 'deny');
    }
  };

  let lastConsentFingerprint: string = '';

  const processConsentChange = (): void => {
    if (!canSetConsent()) {
      log.debug('🚫 Consent BLOCKED - Turnstile verification required');
      return;
    }
    const consentState: Record<string, boolean> = {};
    for (const cookiebotCategory of Object.keys(config.categoryMapping)) {
      consentState[cookiebotCategory] = getCookieBotConsent(cookiebotCategory);
    }
    const fingerprint = JSON.stringify(consentState);
    if (fingerprint === lastConsentFingerprint) return;
    lastConsentFingerprint = fingerprint;

    const gcmUpdates: Record<string, string> = {};
    const wpConsentUpdates: Record<string, string> = {};

    for (const cookiebotCategory of Object.keys(config.categoryMapping)) {
      const wpCategory = config.categoryMapping[cookiebotCategory]!;
      const hasConsent = getCookieBotConsent(cookiebotCategory);
      const consentValue = hasConsent ? 'allow' : 'deny';
      updateWPConsent(wpCategory, consentValue, config, { silent: true });
      wpConsentUpdates[wpCategory] = consentValue;

      const gcmCat = config.gcmMapping[cookiebotCategory];
      if (gcmCat) {
        for (const gcmKey of Object.keys(gcmCat)) {
          const gcmValue = gcmCat[gcmKey]!;
          gcmUpdates[gcmKey] = hasConsent ? gcmValue : 'denied';
        }
      }
    }

    if (Object.keys(gcmUpdates).length > 0) {
      updateGoogleConsentMode(gcmUpdates, config);
      if (typeof w.gtag === 'function') {
        try {
          w.gtag('consent', 'update', gcmUpdates);
        } catch {
          log.debug('⚠️ Error in gtag consent update');
        }
      }
    }
    log.debug('Consent updated', { wp: wpConsentUpdates, gcm: gcmUpdates });
    w._atmConsentApplied = true;
    try {
      document.dispatchEvent(new CustomEvent('atm_consent_applied'));
    } catch {
      // ignore if dispatch fails
    }
  };

  const waitForTurnstile = (callback: () => void, maxAttempts = 150): void => {
    const timeout = maxAttempts * 100;
    const check = (): boolean => {
      if (!isTurnstileScriptLoaded()) return true;
      if (typeof w.atmTurnstileStatus === 'undefined') return false;
      const s = w.atmTurnstileStatus!;
      return (s.verified === true && s.serverVerified === true) || s.failed === true;
    };
    waitFor(check, { interval: 100, timeout })
      .then(callback)
      .catch(() => {
        log.debug('Turnstile wait timeout or unknown state');
        callback();
      });
  };

  const handleURLPassthrough = (): void => {
    if (!config.urlPassthroughClean && !config.urlPassthroughPreserveUtms) return;
    const urlParams = new URLSearchParams(w.location.search);
    const cookiebotParams = ['CookieConsent', 'cbid', 'marketing', 'statistics', 'preferences'];
    let hasCookiebotParams = false;
    const utmParams: Record<string, string> = {};
    if (config.urlPassthroughPreserveUtms) {
      for (const param of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
        const v = urlParams.get(param);
        if (v) utmParams[param] = v;
      }
    }
    for (const param of cookiebotParams) {
      if (urlParams.has(param)) hasCookiebotParams = true;
    }
    if (hasCookiebotParams && config.urlPassthroughClean) {
      const newUrl = new URL(w.location.href);
      for (const param of cookiebotParams) newUrl.searchParams.delete(param);
      for (const [k, v] of Object.entries(utmParams)) newUrl.searchParams.set(k, v);
      w.history.replaceState({}, '', newUrl.toString());
      log.debug('Cleaned URL pass-through parameters');
    }
  };

  const init = (): void => {
    if (typeof w.wp_consent_type === 'undefined') {
      w.wp_consent_type = config.consentType || 'optin';
    }

    if (typeof w.atmTurnstileStatus !== 'undefined') {
      if (w.atmTurnstileStatus!.failed === true) {
        w._atmTurnstileFailed = true;
        (w as { _atmTurnstileVerified?: boolean })._atmTurnstileVerified = false;
        sessionStorage.setItem('atm_turnstile_failed', 'true');
        sessionStorage.setItem('atm_turnstile_verified', 'false');
      } else if (
        w.atmTurnstileStatus!.verified === true &&
        w.atmTurnstileStatus!.serverVerified === true
      ) {
        w._atmTurnstileFailed = false;
        (w as { _atmTurnstileVerified?: boolean })._atmTurnstileVerified = true;
        sessionStorage.setItem('atm_turnstile_failed', 'false');
        sessionStorage.setItem('atm_turnstile_verified', 'true');
      }
    }

    if (!w._atmTurnstileStatusListenerAttached) {
      w._atmTurnstileStatusListenerAttached = true;
      document.addEventListener('atm_turnstile_status_changed', (e: Event) => {
        const status = (e as CustomEvent).detail as {
          verified?: boolean;
          serverVerified?: boolean;
          failed?: boolean;
        };
        if (status.verified === true && status.serverVerified === true) {
          (w as { _atmTurnstileVerified?: boolean })._atmTurnstileVerified = true;
          sessionStorage.setItem('atm_turnstile_verified', 'true');
          log.debug('✅ Turnstile verified (client + server) - applying consent');
          if (canSetConsent()) {
            if (shouldApplyRowDefaultConsent()) {
              applyRowDefaultConsent();
            } else {
              log.debug('🌍 GDPR or CA region enabled - not auto-granting ROW; consent from CookieBot only');
            }
            if (isCookieBotAvailable()) processConsentChange();
          }
        }
        if (status.failed === true) {
          log.debug('❌ Turnstile failed - denying all consent IMMEDIATELY');
          (w as { _atmTurnstileVerified?: boolean })._atmTurnstileVerified = false;
          sessionStorage.setItem('atm_turnstile_verified', 'false');
          w._atmTurnstileFailed = true;
          sessionStorage.setItem('atm_turnstile_failed', 'true');
          if (typeof w.wp_set_consent === 'function') {
            w.wp_set_consent('necessary', 'allow');
            w.wp_set_consent('preferences', 'deny');
            w.wp_set_consent('statistics', 'deny');
            w.wp_set_consent('marketing', 'deny');
          }
          if (typeof w.gtag !== 'undefined') {
            w.gtag!('consent', 'update', {
              ad_storage: 'denied',
              ad_user_data: 'denied',
              ad_personalization: 'denied',
              analytics_storage: 'denied',
              functionality_storage: 'granted',
              personalization_storage: 'denied',
              security_storage: 'granted',
            });
          }
        }
        if (
          status.verified === true &&
          status.serverVerified === true &&
          status.failed === false
        ) {
          w._atmTurnstileFailed = false;
          (w as { _atmTurnstileVerified?: boolean })._atmTurnstileVerified = true;
          sessionStorage.setItem('atm_turnstile_failed', 'false');
          sessionStorage.setItem('atm_turnstile_verified', 'true');
        }
      });
    }

    waitForCookieBot(() => {
      if (typeof w.Cookiebot !== 'undefined' && w.Cookiebot.onAccept) {
        w.Cookiebot.onAccept(() => {
          log.debug('🍪 CookieBot: User accepted (onAccept callback)');
          if (canSetConsent()) {
            log.debug('✅ Turnstile verified - processing consent immediately');
            processConsentChange();
          } else {
            log.debug('⏳ Turnstile not ready - waiting...');
            waitForTurnstile(() => {
              if (canSetConsent()) processConsentChange();
              else log.debug('🚫 Consent processing blocked - Turnstile verification failed');
            });
          }
        });
      }
      if (typeof w.Cookiebot !== 'undefined' && w.Cookiebot.onDecline) {
        w.Cookiebot.onDecline(() => {
          log.debug('🍪 CookieBot: User declined');
          waitForTurnstile(() => {
            if (canSetConsent()) processConsentChange();
          });
        });
      }
      if (typeof w.Cookiebot !== 'undefined' && w.Cookiebot.onRevoke) {
        w.Cookiebot.onRevoke(() => {
          log.debug('🍪 CookieBot: User revoked consent');
          waitForTurnstile(() => {
            if (canSetConsent()) processConsentChange();
          });
        });
      }

      document.addEventListener('CookiebotOnAccept', () => {
        log.debug('🍪 CookieBot event: Accept (CookiebotOnAccept event)');
        if (canSetConsent()) {
          processConsentChange();
        } else {
          waitForTurnstile(() => {
            if (canSetConsent()) processConsentChange();
          });
        }
      });
      document.addEventListener('CookiebotOnDecline', () => {
        log.debug('🍪 CookieBot event: Decline');
        waitForTurnstile(() => {
          if (canSetConsent()) processConsentChange();
        });
      });
      document.addEventListener('CookiebotOnRevoke', () => {
        log.debug('🍪 CookieBot event: Revoke');
        waitForTurnstile(() => {
          if (canSetConsent()) processConsentChange();
        });
      });

      if (typeof w.Cookiebot !== 'undefined' && w.Cookiebot.consent) {
        let lastConsent = JSON.stringify(w.Cookiebot.consent);
        setInterval(() => {
          const currentConsent = JSON.stringify(w.Cookiebot!.consent);
          if (currentConsent !== lastConsent) {
            log.debug('🍪 CookieBot consent changed (detected via polling)');
            lastConsent = currentConsent;
            waitForTurnstile(() => {
              if (canSetConsent()) processConsentChange();
            });
          }
        }, 1000);
      }

      waitForTurnstile(() => {
        if (canSetConsent()) {
          if (shouldApplyRowDefaultConsent()) applyRowDefaultConsent();
          if (isCookieBotAvailable()) processConsentChange();
        } else {
          log.debug('🚫 Initial consent processing blocked - Turnstile verification failed');
        }
      });
    });

    handleURLPassthrough();
  };

  onReady(init);
};

run();
